import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import { toasterApi } from '@terra/ui-design-system';
import { applicationsClient } from '@terra/shared-rest-clients-applications';
import {
  buildApplicationDomainsFromScope,
  toDomainConfigInputList,
  toPersistedDomainConfigList,
  type DomainConfigInputSource,
} from '@terra/shared-contracts-applications';
import { getCurrentGatewayUrl } from '../../../utils/environmentDetection';
import { getAuthHeaders } from '../../../utils/authHeaders';
import { useProjectStore } from '../../../store/projectStore';
import { APPLICATION_BY_ID_QUERY_KEY } from '../../../hooks/api/useApplicationById';
import { getApplicationDomainsQueryKey } from '../../../hooks/api/useApplicationDomains';

export type UpdateTargetSettingsVariables = {
  domains?: DomainConfigInputSource[];
  url?: string;
  skipDomainQueryInvalidation?: boolean;
  skipSaveToast?: boolean;
};

export function useTargetSettingsMutation({
  applicationId,
  tenantId,
}: {
  applicationId?: string;
  tenantId?: string;
}) {
  const t = useTranslations('settings');
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ domains, url }: UpdateTargetSettingsVariables) => {
      if (!applicationId || !tenantId) {
        throw new Error('No target or company selected');
      }

      const authHeaders = await getAuthHeaders();
      const overrideClientOptions = {
        baseUrl: `${getCurrentGatewayUrl()}/orchestrator`,
      };

      if (domains || url !== undefined) {
        const response = await applicationsClient.updateApplication({
          params: { id: applicationId },
          body: {
            ...(domains !== undefined
              ? { domains: toDomainConfigInputList(domains) }
              : {}),
            ...(url !== undefined ? { url } : {}),
          },
          overrideClientOptions,
          extraHeaders: { ...authHeaders },
        });

        if (response.status !== 200) {
          throw new Error('Failed to update application');
        }
      }

      return true;
    },
    onSuccess: (_result, variables) => {
      const { selectedTarget: currentApp, updateSelectedTarget } =
        useProjectStore.getState();

      if (variables.domains !== undefined) {
        if (applicationId) {
          queryClient.setQueryData(
            [APPLICATION_BY_ID_QUERY_KEY, applicationId],
            (
              currentApplication:
                | { domains?: typeof variables.domains }
                | undefined,
            ) =>
              currentApplication
                ? { ...currentApplication, domains: variables.domains }
                : currentApplication,
          );
        }

        if (!variables.skipDomainQueryInvalidation) {
          queryClient.invalidateQueries({
            queryKey: getApplicationDomainsQueryKey(applicationId),
          });
          queryClient.invalidateQueries({
            queryKey: [APPLICATION_BY_ID_QUERY_KEY, applicationId],
          });
        }

        if (currentApp) {
          updateSelectedTarget({
            ...currentApp,
            domains: toPersistedDomainConfigList({
              domains: variables.domains,
              previousDomains: currentApp.domains,
            }),
          });
        }
      }

      if (variables.url !== undefined) {
        queryClient.invalidateQueries({
          queryKey: ['application-by-id', applicationId],
        });
        queryClient.invalidateQueries({ queryKey: ['applications'] });
        queryClient.invalidateQueries({
          queryKey: getApplicationDomainsQueryKey(applicationId),
        });
        const { selectedTarget: latestApp } = useProjectStore.getState();

        if (latestApp) {
          updateSelectedTarget({
            ...latestApp,
            url: variables.url,
            redirectToUrl: null,
            domains: toPersistedDomainConfigList({
              domains: buildApplicationDomainsFromScope({
                applicationUrl: variables.url,
                domains: latestApp.domains ?? [],
                previousApplicationUrl: latestApp.url,
                previousRedirectToUrl: latestApp.redirectToUrl,
              }),
              previousDomains: latestApp.domains,
            }),
          });
        }
      }

      if (!variables.skipSaveToast) {
        toasterApi.success({
          title: t('settingsSaved'),
          description: t('settingsSavedDescription'),
        });
      }
    },
    onError: (error) => {
      console.error('Failed to save settings:', error);
      toasterApi.error({
        title: t('saveFailed'),
        description: t('saveFailedDescription'),
      });
    },
  });
}
